import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import LoginPage from "./pages/LoginPage";
import MiniLoginPage from "./pages/MiniLoginPage";
import LandingPage from "./pages/LandingPage";
import PartsPage from "./pages/PartsPage";
import CheckoutPage from "./pages/CheckoutPage";
import CustomerPage from "./pages/CustomerPage";
import ReceptionPage from "./pages/ReceptionPage";
import MechanicPage from "./pages/MechanicPage";
import TesterPage from "./pages/TesterPage";
import AdminPage from "./pages/AdminPage";
import EmployeePage from "./pages/EmployeePage";
import ShopPage from "./pages/ShopPage";
import ShopPaymentPage from "./pages/ShopPaymentPage";
import PlatformAdminPage from "./pages/PlatformAdminPage";
import QaLabPage from "./pages/QaLabPage";
import LedgerPage from "./pages/LedgerPage";
import CheckoutStatusPage from "./pages/CheckoutStatusPage";
import LedgerPaymentPage from "./pages/LedgerPaymentPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import { Toaster } from "./components/ui/sonner";
import PWAInstallBanner from "./components/PWAInstallBanner";
import PWAManifestLink from "./components/PWAManifestLink";
import InternalChatWidget from "./components/InternalChatWidget";
import ScrollToTopButton from "./components/ScrollToTopButton";
import { adminRoles, roleHomePath } from "./lib/roles";
import { hasModule, moduleHome, roleModule } from "./lib/subscriptionModules";
import api from "./lib/api";

const RoleRoute = ({ role, children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-zinc-950 grid place-items-center text-zinc-400 font-mono text-xs">Loading...</div>;
  if (!user) return <Navigate to="/terminal" replace />;
  if (user.role !== role) return <Navigate to={moduleHome(user) || roleHomePath(user.role)} replace />;
  const module = roleModule(role);
  if (module && !hasModule(user, module)) return <Navigate to={moduleHome(user)} replace />;
  return children;
};

const AnyRoleRoute = ({ roles, children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-zinc-950 grid place-items-center text-zinc-400 font-mono text-xs">Loading...</div>;
  if (!user) return <Navigate to="/terminal" replace />;
  if (!roles.includes(user.role)) return <Navigate to={moduleHome(user) || roleHomePath(user.role)} replace />;
  return children;
};

const EmployeeRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/terminal" replace />;
  if (!["mechanic", "reception", "tester", "admin", "tenant_admin", "branch_admin", "shopkeeper"].includes(user.role)) return <Navigate to={moduleHome(user) || roleHomePath(user.role)} replace />;
  if (!hasModule(user, "hr")) return <Navigate to={moduleHome(user)} replace />;
  return children;
};

const ModuleRoute = ({ module, children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-zinc-950 grid place-items-center text-zinc-400 font-mono text-xs">Loading...</div>;
  if (!user) return <Navigate to={module === "billing" ? "/mini" : "/terminal"} replace />;
  if (!hasModule(user, module)) return <Navigate to={moduleHome(user)} replace />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <RootQueryRedirect />
        <PWAManifestLink />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/garage/:tenantSlug" element={<Navigate to="/" replace />} />
          <Route path="/garage/:tenantSlug/parts" element={<Navigate to="/parts" replace />} />
          <Route path="/garage/:tenantSlug/track" element={<Navigate to="/track" replace />} />
          <Route path="/parts" element={<PartsPage />} />
          {/* Secure customer-facing tracker with email OTP on the page. */}
          <Route path="/track" element={<CustomerPage />} />
          <Route path="/login" element={<Navigate to="/terminal" replace />} />
          <Route path="/customer" element={<Navigate to="/track" replace />} />
          {/* Terminal sign-in. */}
          <Route path="/terminal" element={<LoginPage />} />
          <Route path="/mini" element={<MiniLoginPage />} />
          <Route path="/mini/login" element={<Navigate to="/mini" replace />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/staff" element={<Navigate to="/terminal" replace />} />
          <Route path="/pay/:bookingId" element={<CheckoutPage />} />
          <Route path="/checkout/status/:id" element={<CheckoutStatusPage />} />
          <Route path="/checkout/:bookingId" element={<CheckoutPage />} />
          <Route path="/shop/pay/:saleId" element={<ShopPaymentPage />} />
          <Route path="/ledger/pay/:id" element={<LedgerPaymentPage />} />
          <Route path="/reception" element={<RoleRoute role="reception"><ReceptionPage /></RoleRoute>} />
          <Route path="/mechanic" element={<RoleRoute role="mechanic"><MechanicPage /></RoleRoute>} />
          <Route path="/tester" element={<RoleRoute role="tester"><TesterPage /></RoleRoute>} />
          <Route path="/admin" element={<AnyRoleRoute roles={adminRoles}><AdminPage /></AnyRoleRoute>} />
          <Route path="/platform" element={<RoleRoute role="platform_admin"><PlatformAdminPage /></RoleRoute>} />
          <Route path="/qa" element={<AnyRoleRoute roles={["qa", "platform_admin"]}><QaLabPage /></AnyRoleRoute>} />
          <Route path="/shopkeeper" element={<RoleRoute role="shopkeeper"><ShopPage /></RoleRoute>} />
          <Route path="/shop" element={<ModuleRoute module="shop"><ShopPage /></ModuleRoute>} />
          <Route path="/ledger" element={<ModuleRoute module="billing"><LedgerPage /></ModuleRoute>} />
          <Route path="/employee" element={<EmployeeRoute><EmployeePage /></EmployeeRoute>} />
        </Routes>
        <InternalChatWidget />
        <ScrollToTopButton />
        <StorageFullWatcher />
        <StorageFullModal />
        <SubscribeRequiredModal />
        <Toaster theme="dark" position="top-right" duration={2500} visibleToasts={2} />
        <PWAInstallBanner />
      </BrowserRouter>
    </AuthProvider>
  );
}

function RootQueryRedirect() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkoutId = params.get("checkout_status_id");
    if (!checkoutId) return;
    const status = params.get("status") || "";
    window.history.replaceState(null, "", `/checkout/status/${checkoutId}${status ? `?status=${encodeURIComponent(status)}` : ""}`);
  }, []);
  return null;
}

function StorageFullWatcher() {
  const { user } = useAuth();
  const [storage, setStorage] = useState(null);
  useEffect(() => {
    if (!user || user.role === "platform_admin" || user.role === "customer_tracker" || user.role === "qa") return undefined;
    let alive = true;
    api.get("/admin/subscription/me")
      .then((res) => {
        if (!alive) return;
        const data = res.data || {};
        if (data.storage_limit_reached) {
          setStorage(data.storage_usage || {});
          window.dispatchEvent(new CustomEvent("macjit:storage-full", {
            detail: { message: "Storage is full. New service bookings and shop transactions are paused. Contact admin to archive old records or buy more storage." },
          }));
        } else {
          setStorage(null);
        }
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [user]);
  if (!storage) return null;
  return (
    <div className="fixed left-4 right-4 top-20 z-50 border border-red-500/50 bg-red-950/90 px-4 py-3 text-red-50 shadow-2xl shadow-black/40 backdrop-blur md:left-auto md:max-w-xl">
      <p className="font-display text-sm font-black uppercase tracking-wide">Storage full</p>
      <p className="mt-1 text-xs leading-relaxed text-red-100/90">
        Used {storage.used_gb ?? "-"}GB of {storage.limit_gb ?? "-"}GB. New service bookings and shop transactions are paused. Contact admin to archive old records or buy more storage.
      </p>
    </div>
  );
}

function StorageFullModal() {
  const [state, setState] = useState(null);
  useEffect(() => {
    const handler = (event) => setState(event.detail || {});
    window.addEventListener("macjit:storage-full", handler);
    return () => window.removeEventListener("macjit:storage-full", handler);
  }, []);
  if (!state) return null;
  return (
    <div className="fixed inset-0 z-[101] grid place-items-center bg-black/75 px-4 backdrop-blur-sm">
      <div className="relative w-full max-w-lg border border-orange-500/60 bg-zinc-950 p-6 shadow-2xl shadow-orange-950/40">
        <button
          onClick={() => setState(null)}
          className="absolute right-3 top-3 grid h-9 w-9 place-items-center border border-zinc-800 text-orange-400 hover:border-orange-500 hover:bg-orange-500 hover:text-black"
          aria-label="Close storage warning"
        >
          ×
        </button>
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-red-400">Storage full</p>
        <h2 className="mt-3 font-display text-3xl font-black tracking-tight text-white">Action paused</h2>
        <p className="mt-3 text-sm leading-relaxed text-zinc-400">
          {state.message || "Storage is full. New records cannot be created. Contact admin to archive old records or buy more storage."}
        </p>
        <p className="mt-5 border border-zinc-800 bg-black p-3 text-xs leading-relaxed text-zinc-500">
          Payment gateway and subscription payment remain available.
        </p>
      </div>
    </div>
  );
}

function SubscribeRequiredModal() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const handler = (event) => setState(event.detail || {});
    window.addEventListener("macjit:subscribe-required", handler);
    return () => window.removeEventListener("macjit:subscribe-required", handler);
  }, []);
  if (!state) return null;
  const subscribe = async () => {
    setBusy(true);
    try {
      const res = await api.post("/admin/subscription/checkout");
      const url = res.data?.payment_url || res.data?.terminal_url || "/#plans";
      window.location.href = url;
    } catch {
      window.location.href = "/#plans";
    } finally {
      setBusy(false);
      setState(null);
    }
  };
  return (
    <div className="fixed inset-0 z-[100] grid place-items-center bg-black/75 px-4 backdrop-blur-sm">
      <div className="w-full max-w-md border border-orange-500/60 bg-zinc-950 p-6 shadow-2xl shadow-orange-950/40">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Subscribe now</p>
        <h2 className="mt-3 font-display text-3xl font-black tracking-tight text-white">Free trial reached</h2>
        <p className="mt-3 text-sm leading-relaxed text-zinc-400">
          {state.message || "You have reached your free trial limit. Subscribe now to continue using Terminal."}
        </p>
        <div className="mt-6 grid grid-cols-2 gap-2">
          <button onClick={() => setState(null)} className="border border-zinc-700 px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-zinc-300 hover:border-zinc-400">
            Later
          </button>
          <button onClick={subscribe} disabled={busy} className="bg-orange-500 px-4 py-3 text-center font-display text-xs font-black uppercase tracking-widest text-black hover:bg-orange-400 disabled:opacity-50">
            {busy ? "Opening..." : "Subscribe"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default App;
